import { useMemo, useState } from "react";
import { hotEvents, categories, type HotEvent } from "@/mocks/events";

const statusColor: Record<HotEvent["status"], string> = {
  fajiao: "bg-primary-100 text-primary-700",
  fanzhuan: "bg-accent-100 text-accent-700",
  luoding: "bg-secondary-200 text-secondary-800",
  chixu: "bg-background-200 text-foreground-700",
};

export default function EventFeed() {
  const [cat, setCat] = useState("全部");
  const [kw, setKw] = useState("");
  const [selected, setSelected] = useState<HotEvent | null>(null);

  const list = useMemo(() => {
    let arr = hotEvents;
    if (cat !== "全部") arr = arr.filter((e) => e.category === cat);
    if (kw.trim()) {
      const q = kw.trim().toLowerCase();
      arr = arr.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.summary.toLowerCase().includes(q) ||
          e.tag.includes(kw.trim())
      );
    }
    return arr;
  }, [cat, kw]);

  const goToTimeline = () => {
    setSelected(null);
    const el = document.querySelector("#timeline");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="feed" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 text-sm font-bold text-primary-600">
              <span className="h-px w-6 bg-primary-500" />
              LIVE 实时现场
            </p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              正在发酵的瓜点
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              每一个事件都配有来源、热度与更新频率，点击卡片可查看本事件的追踪要点。
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="筛选事件关键词…"
              aria-label="搜索事件关键词"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
            />
          </div>
        </div>

        {/* category filter */}
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                cat === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-secondary-100 text-foreground-700 hover:bg-secondary-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* grid */}
        {list.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-600">这个分类下暂时还没有匹配的瓜点，换个关键词试试吧。</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {list.map((e, idx) => (
              <article
                key={e.id}
                onClick={() => setSelected(e)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={e.img}
                    alt={e.imgAlt}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-background-950/70 px-2.5 py-1 text-xs font-bold text-background-50 backdrop-blur">
                    <i className="ri-fire-fill text-primary-400" />
                    {e.heat}
                  </span>
                  {idx < 3 && (
                    <span className="absolute right-3 top-3 rounded-full bg-primary-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                      热门
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${statusColor[e.status]}`}>
                      {e.statusText}
                    </span>
                    <span className="text-[11px] text-foreground-400">{e.category}</span>
                  </div>
                  <h3 className="mt-2.5 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                    {e.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{e.summary}</p>
                  <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-refresh-line text-primary-500" />
                      {e.updates} 次更新
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {e.views} 围观
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-foreground-500">
          共找到 <b className="text-foreground-700">{list.length}</b> 个匹配的瓜点 · 数据每 30 分钟自动刷新
        </p>
      </div>

      {/* detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${selected.title} 追踪详情`}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-background-50"
          >
            <div className="relative h-44 md:h-52">
              <img
                src={selected.img}
                alt={selected.imgAlt}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent" />
              <button
                onClick={() => setSelected(null)}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-foreground-800 hover:bg-background-50"
                aria-label="关闭详情"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-5 md:p-7">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusColor[selected.status]}`}>
                  {selected.statusText}
                </span>
                <span className="text-xs text-foreground-500">{selected.category}</span>
              </div>
              <h3 className="mt-3 font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                {selected.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{selected.summary}</p>

              <ul className="mt-4 grid grid-cols-1 gap-2 text-sm text-foreground-700 sm:grid-cols-2">
                <li className="flex items-center gap-2">
                  <i className="ri-fire-fill text-primary-500" />
                  <span>
                    实时热度 <b className="text-foreground-950">{selected.heat}</b>
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-refresh-line text-accent-600" />
                  <span>
                    已更新 <b className="text-foreground-950">{selected.updates}</b> 次
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-eye-line text-secondary-600" />
                  <span>
                    累计围观 <b className="text-foreground-950">{selected.views}</b>
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-secondary-600" />
                  <span>{selected.time}</span>
                </li>
              </ul>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={goToTimeline}
                  className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看完整反转档案
                </button>
                <a
                  href="mailto:gossip@baozhagua.example.com"
                  className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-center text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  补充线索
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}